// =============================================================================
// API Hooks - TanStack Query Data Fetching
// =============================================================================

import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { useTenantStore, useTenantTimeZone, TenantMembership } from '../store/tenantStore';
import { useAuthStore, User } from '../store/authStore';
import {
  civilDayOf,
  lastInstantOfCivilDay,
  startOfCivilDayInTz,
  todayInTz,
} from '../lib/tenantTime';
import type { CivilDate } from '../lib/tenantTime';
import type {
  Resource,
  ResourceType,
  ServiceDefinition,
  Appointment,
  Customer,
  CustomerAppointmentsResponse,
  PaginatedResponse,
} from '../types';
import type {
  BookingRequest,
  BookingRequestStatus,
  PublicBookingSettings,
} from '../types/public-booking';
import type { OrgBranding } from '../components/common/OrgAvatar';

// ===========================================================================
// Query Keys
// ===========================================================================

export const queryKeys = {
  // Resources
  resourceTypes: ['resourceTypes'] as const,
  // Namespace separato da `resourceTypes`: invalidare la lista dopo una delete
  // non deve far rifetchare l'usage di un tipo appena eliminato (404 in console).
  resourceTypeUsage: (id: string) => ['resourceTypeUsage', id] as const,
  resources: (typeId?: string) => ['resources', { typeId }] as const,
  resource: (id: string) => ['resources', id] as const,
  
  // Services
  services: (category?: string) => ['services', { category }] as const,
  serviceCategories: ['serviceCategories'] as const,
  service: (id: string) => ['services', id] as const,
  
  // Appointments
  appointments: (date: string, resourceId?: string, status?: string) =>
    ['appointments', { date, resourceId, status }] as const,
  // Vista agenda: stesso namespace `appointments` (cosi' le invalidazioni
  // realtime e post-mutazione la coprono gia'), ma chiave distinta dal giorno.
  appointmentsRange: (start: string, end: string, resourceId?: string, status?: string) =>
    ['appointments', 'range', { start, end, resourceId, status }] as const,
  appointmentsUpcoming: (from: string) => ['appointments', 'upcoming', { from }] as const,
  appointment: (id: string) => ['appointments', id] as const,
  
  // Customers
  customers: (search?: string) => ['customers', { search }] as const,
  customer: (id: string) => ['customers', id] as const,
  customerAppointments: (id: string) => ['customers', id, 'appointments'] as const,

  // Dashboard
  dashboardStats: (days: number) => ['dashboardStats', { days }] as const,
  dashboardUtilization: ['dashboardUtilization'] as const,

  // Reports
  reportsUtilization: (params: ReportsUtilizationParams) =>
    ['reportsUtilization', params] as const,
  reportsPeakHours: (params: PeakHoursParams) =>
    ['reportsPeakHours', params] as const,

  // Tenant
  tenantPresets: ['tenantPresets'] as const,
  myInvites: ['myInvites'] as const,
  myDeletedTenants: ['myDeletedTenants'] as const,
};

// ===========================================================================
// Tenant Presets Hooks
// ===========================================================================

export interface TenantPresetSummary {
  id: string;
  icon: string;
  resourceTypes: { name: string; icon?: string; color?: string }[];
  services: { name: string; durationMinutes: number; category?: string }[];
}

export function useTenantPresets(enabled = true) {
  return useQuery({
    queryKey: queryKeys.tenantPresets,
    queryFn: async (): Promise<TenantPresetSummary[]> => {
      const { data } = await apiClient.get('/tenants/presets');
      return data;
    },
    staleTime: 10 * 60 * 1000, // i preset sono statici lato server
    enabled,
  });
}

// ===========================================================================
// Profilo dell'utente corrente (nome, email, password)
// ===========================================================================

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Obbligatoria solo quando cambia l'email: e' anche la credenziale di accesso */
  currentPassword?: string;
}

export function useUpdateProfile() {
  return useMutation({
    mutationFn: async (payload: UpdateProfilePayload) => {
      const { data } = await apiClient.patch('/auth/me', payload);
      return data as { user: User; access_token?: string };
    },
    onSuccess: ({ user, access_token }) => {
      // L'authStore e' la fonte del profilo per tutta la UI (sidebar, header):
      // senza questo resterebbe il nome vecchio fino al prossimo login.
      useAuthStore.getState().updateSession({ user, token: access_token });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) => {
      const { data } = await apiClient.post('/auth/change-password', payload);
      return data as { message: string; access_token: string };
    },
    onSuccess: ({ access_token }) => {
      // Il cambio password revoca tutti i token emessi prima, incluso quello di
      // questa sessione: senza sostituirlo il primo 401 farebbe logout.
      useAuthStore.getState().updateSession({ token: access_token });
    },
  });
}

// ===========================================================================
// Inviti destinati all'utente corrente (cross-tenant)
// ===========================================================================

export interface MyInvite {
  id: string;
  organizationId: string;
  tenantName: string;
  tenantSlug: string;
  role: 'OWNER' | 'ADMIN' | 'WORKER';
  invitedByName?: string | null;
  expiresAt: string;
  createdAt: string;
  branding: OrgBranding;
}

export function useMyInvites(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myInvites,
    queryFn: async (): Promise<MyInvite[]> => {
      const { data } = await apiClient.get('/tenants/my-invites');
      return data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useAcceptMyInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data } = await apiClient.post(`/tenants/my-invites/${inviteId}/accept`);
      return data as { message: string };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myInvites });
    },
  });
}

export function useDeclineMyInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const { data } = await apiClient.post(`/tenants/my-invites/${inviteId}/decline`);
      return data as { message: string };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myInvites });
    },
  });
}

/**
 * Ricarica le memberships dal server e le riflette nel tenantStore.
 * Da chiamare dopo l'accettazione di un invito.
 */
export async function refreshMyMemberships(): Promise<TenantMembership[]> {
  const { data } = await apiClient.get<
    Array<TenantMembership & { id: string; isActive: boolean }>
  >('/tenants/my');
  const memberships: TenantMembership[] = data.map((m) => ({
    organizationId: m.organizationId,
    tenantName: m.tenantName,
    tenantSlug: m.tenantSlug,
    timezone: m.timezone,
    role: m.role,
    branding: m.branding,
  }));
  useTenantStore.getState().setMemberships(memberships);
  return memberships;
}

// ===========================================================================
// Resources Hooks
// ===========================================================================

export function useResourceTypes() {
  return useQuery({
    queryKey: queryKeys.resourceTypes,
    queryFn: async (): Promise<ResourceType[]> => {
      const { data } = await apiClient.get('/resources/types');
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - resource types don't change often
  });
}

export function useResources(typeId?: string) {
  // Senza tenant attivo la GET /resources torna 403: succede sulle pagine fuori
  // da un'organizzazione (login, selezione tenant, onboarding, profilo standalone).
  const activeTenantId = useTenantStore((s) => s.activeTenantId);

  return useQuery({
    queryKey: queryKeys.resources(typeId),
    queryFn: async (): Promise<Resource[]> => {
      const params = typeId ? { typeId } : {};
      const { data } = await apiClient.get('/resources', { params });
      return data;
    },
    enabled: !!activeTenantId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useResource(id: string) {
  return useQuery({
    queryKey: queryKeys.resource(id),
    queryFn: async (): Promise<Resource> => {
      const { data } = await apiClient.get(`/resources/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

// ===========================================================================
// Resources Mutations
// ===========================================================================

export interface CreateResourceData {
  name: string;
  resourceTypeId: string;
  code?: string;
  description?: string;
  color?: string;
  customAttributes?: Record<string, unknown>;
  isBookable?: boolean;
  isPubliclySelectable?: boolean;
}

export function useCreateResource() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateResourceData): Promise<Resource> => {
      const response = await apiClient.post('/resources', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
}

export function useUpdateResource() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: CreateResourceData & { id: string }): Promise<Resource> => {
      const response = await apiClient.put(`/resources/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
}

export function useDeleteResource() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/resources/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
}

// ===========================================================================
// Resource Weekly Schedule
// ===========================================================================

export interface WeeklyScheduleItem {
  dayOfWeek: number;
  dayName: string;
  isWorkingDay: boolean;
  startTime?: string;
  endTime?: string;
}

// Input per update (dayName non va inviato al backend)
export interface WeeklyScheduleInput {
  dayOfWeek: number;
  isWorkingDay: boolean;
  startTime?: string;
  endTime?: string;
}

export function useResourceSchedule(resourceId: string) {
  return useQuery({
    queryKey: ['resource', resourceId, 'schedule'],
    queryFn: async (): Promise<WeeklyScheduleItem[]> => {
      const { data } = await apiClient.get(`/resources/${resourceId}/schedule`);
      return data;
    },
    enabled: !!resourceId,
  });
}

export function useUpdateResourceSchedule() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ resourceId, schedule }: { resourceId: string; schedule: WeeklyScheduleInput[] }): Promise<WeeklyScheduleItem[]> => {
      const response = await apiClient.put(`/resources/${resourceId}/schedule`, { schedule });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['resource', variables.resourceId, 'schedule'] });
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
  });
}

// ===========================================================================
// Resource Exceptions (Assenze / Presenze aggiuntive)
// ===========================================================================

/** Causale di assenza (personalizzabile per organizzazione). */
export interface AbsenceReason {
  id: string;
  name: string;
  color: string;
  icon: string;
  isActive: boolean;
  sortOrder: number;
}

/** Sintesi della causale allegata a un'eccezione (per colore/icona/nome). */
export interface ExceptionReasonSummary {
  id: string;
  name: string;
  color: string;
  icon: string;
}

export interface ResourceException {
  id: string;
  resourceId: string;
  reasonId: string | null;
  reason: ExceptionReasonSummary | null;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  title: string | null;
  notes: string | null;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ExceptionInput {
  reasonId?: string | null;
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
  title?: string | null;
  notes?: string | null;
  isAvailable?: boolean;
}

/**
 * Eccezioni di tutte le risorse dell'organizzazione che intersecano una data.
 * Usato dal calendario per riflettere assenze/presenze aggiuntive.
 */
export function useDayExceptions(date: Date | string) {
  // Il carrier trasporta gia' la data civile del tenant: va letta con i getter
  // locali, non riformattata come istante.
  const dateStr = typeof date === 'string' ? date.slice(0, 10) : civilDayOf(date);
  return useQuery({
    queryKey: ['resource', 'exceptions', 'day', dateStr],
    queryFn: async (): Promise<ResourceException[]> => {
      const { data } = await apiClient.get('/resources/exceptions', { params: { date: dateStr } });
      return data;
    },
    staleTime: 60 * 1000,
  });
}

export interface ExceptionConflict {
  appointmentId: string;
  allocationId: string;
  startTime: string;
  endTime: string;
  status: string;
  title: string;
  serviceName: string | null;
  customerName: string | null;
}

export interface ExceptionConflictQuery {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
}

/**
 * Appuntamenti attivi della risorsa che ricadrebbero in un'assenza col periodo dato.
 * Usato per avvisare l'utente di riorganizzarli prima di creare l'assenza.
 */
export function useExceptionConflicts(
  resourceId: string | undefined,
  query: ExceptionConflictQuery | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['resource', resourceId, 'exception-conflicts', query],
    queryFn: async (): Promise<ExceptionConflict[]> => {
      const { data } = await apiClient.post(`/resources/${resourceId}/exceptions/conflicts`, query);
      return data;
    },
    enabled: enabled && !!resourceId && !!query,
    staleTime: 10 * 1000,
  });
}

export function useResourceExceptions(resourceId: string) {
  return useQuery({
    queryKey: ['resource', resourceId, 'exceptions'],
    queryFn: async (): Promise<ResourceException[]> => {
      const { data } = await apiClient.get(`/resources/${resourceId}/exceptions`);
      return data;
    },
    enabled: !!resourceId,
  });
}

/**
 * Tutte le eccezioni dell'organizzazione (per la gestione centralizzata).
 * Di default solo quelle non ancora concluse.
 */
export function useAllExceptions(upcoming = true) {
  return useQuery({
    queryKey: ['resource', 'exceptions', 'all', upcoming],
    queryFn: async (): Promise<ResourceException[]> => {
      const params = upcoming ? { upcoming: 'true' } : {};
      const { data } = await apiClient.get('/resources/exceptions', { params });
      return data;
    },
    staleTime: 30 * 1000,
  });
}

export function useCreateResourceException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      resourceId,
      ...input
    }: ExceptionInput & { resourceId: string }): Promise<ResourceException> => {
      const { data } = await apiClient.post(`/resources/${resourceId}/exceptions`, input);
      return data;
    },
    onSuccess: () => {
      // Invalida tutte le query legate alle eccezioni (pannello risorsa, lista
      // centralizzata, bande del calendario).
      queryClient.invalidateQueries({ queryKey: ['resource'] });
    },
  });
}

export function useUpdateResourceException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      resourceId,
      exceptionId,
      ...input
    }: Partial<ExceptionInput> & { resourceId: string; exceptionId: string }): Promise<ResourceException> => {
      const { data } = await apiClient.put(`/resources/${resourceId}/exceptions/${exceptionId}`, input);
      return data;
    },
    onSuccess: () => {
      // Invalida tutte le query legate alle eccezioni (pannello risorsa, lista
      // centralizzata, bande del calendario).
      queryClient.invalidateQueries({ queryKey: ['resource'] });
    },
  });
}

export function useDeleteResourceException() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      resourceId,
      exceptionId,
    }: {
      resourceId: string;
      exceptionId: string;
    }): Promise<void> => {
      await apiClient.delete(`/resources/${resourceId}/exceptions/${exceptionId}`);
    },
    onSuccess: () => {
      // Invalida tutte le query legate alle eccezioni (pannello risorsa, lista
      // centralizzata, bande del calendario).
      queryClient.invalidateQueries({ queryKey: ['resource'] });
    },
  });
}

// ===========================================================================
// Absence Reasons (Causali di assenza personalizzabili)
// ===========================================================================

export interface AbsenceReasonInput {
  name: string;
  color?: string;
  icon?: string;
  isActive?: boolean;
  sortOrder?: number;
}

/**
 * Causali di assenza dell'organizzazione. `onlyActive` limita a quelle attive
 * (usato dal selettore nel form assenza).
 */
export function useAbsenceReasons(onlyActive = false) {
  return useQuery({
    queryKey: ['absence-reasons', onlyActive],
    queryFn: async (): Promise<AbsenceReason[]> => {
      const params = onlyActive ? { active: 'true' } : {};
      const { data } = await apiClient.get('/absence-reasons', { params });
      return data;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateAbsenceReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AbsenceReasonInput): Promise<AbsenceReason> => {
      const { data } = await apiClient.post('/absence-reasons', input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absence-reasons'] });
    },
  });
}

export function useUpdateAbsenceReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<AbsenceReasonInput> & { id: string }): Promise<AbsenceReason> => {
      const { data } = await apiClient.patch(`/absence-reasons/${id}`, input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absence-reasons'] });
      // Le eccezioni mostrano nome/colore/icona della causale: aggiorna anche loro.
      queryClient.invalidateQueries({ queryKey: ['resource'] });
    },
  });
}

export function useDeleteAbsenceReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/absence-reasons/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absence-reasons'] });
      queryClient.invalidateQueries({ queryKey: ['resource'] });
    },
  });
}

// ===========================================================================
// Calendar Presets (Viste salvate dei filtri del calendario)
// ===========================================================================
// I preset appartengono alla coppia utente-organizzazione: cambiando tenant si
// ricarica un elenco diverso (la query key non lo cita perche' apiClient manda
// gia' x-tenant-id e il cambio tenant svuota la cache).

/** Filtri salvati: stessa forma dello stato dello scheduler. */
export interface CalendarPresetFilters {
  /** `null` = nessun filtro sui tipi (include quelli creati dopo il salvataggio). */
  resourceTypeIds: string[] | null;
  /** `[]` = tutte le risorse dei tipi visibili. */
  resourceIds: string[];
  searchTerm: string;
}

export interface CalendarPreset {
  id: string;
  name: string;
  filters: CalendarPresetFilters;
  sortOrder: number;
}

export interface CalendarPresetInput {
  name: string;
  filters: CalendarPresetFilters;
  sortOrder?: number;
}

export function useCalendarPresets() {
  return useQuery({
    queryKey: ['calendar-presets'],
    queryFn: async (): Promise<CalendarPreset[]> => {
      const { data } = await apiClient.get('/calendar-presets');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCalendarPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CalendarPresetInput): Promise<CalendarPreset> => {
      const { data } = await apiClient.post('/calendar-presets', input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-presets'] });
    },
  });
}

export function useUpdateCalendarPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: Partial<CalendarPresetInput> & { id: string }): Promise<CalendarPreset> => {
      const { data } = await apiClient.patch(`/calendar-presets/${id}`, input);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-presets'] });
    },
  });
}

export function useDeleteCalendarPreset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/calendar-presets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-presets'] });
    },
  });
}

export interface CreateResourceTypeData {
  name: string;
  code: string;
  description?: string;
  color?: string;
  /** Nome icona lucide; `null` azzera l'icona (si mostra l'iniziale del nome). */
  icon?: string | null;
  attributesSchema?: unknown[]; // Array of attribute field definitions
}

export function useCreateResourceType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateResourceTypeData): Promise<ResourceType> => {
      const response = await apiClient.post('/resources/types', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.resourceTypes });
    },
  });
}

export function useUpdateResourceType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: CreateResourceTypeData & { id: string }): Promise<ResourceType> => {
      const response = await apiClient.put(`/resources/types/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.resourceTypes });
    },
  });
}

/** Entità che impediscono l'eliminazione di un tipo di risorsa. */
export interface ResourceTypeUsage {
  resources: { id: string; name: string }[];
  services: { id: string; name: string }[];
  canDelete: boolean;
}

/**
 * Pre-check prima di eliminare un tipo: elenca risorse di quel tipo e servizi
 * che lo richiedono. Passare `null` finché non c'è un tipo da verificare.
 */
export function useResourceTypeUsage(id: string | null) {
  return useQuery({
    queryKey: queryKeys.resourceTypeUsage(id ?? ''),
    queryFn: async (): Promise<ResourceTypeUsage> => {
      const { data } = await apiClient.get(`/resources/types/${id}/usage`);
      return data;
    },
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useDeleteResourceType() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/resources/types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.resourceTypes });
    },
  });
}

// ===========================================================================
// Services Hooks
// ===========================================================================

export function useServices(category?: string) {
  return useQuery({
    queryKey: queryKeys.services(category),
    queryFn: async (): Promise<ServiceDefinition[]> => {
      const params = category ? { category } : {};
      const { data } = await apiClient.get('/services', { params });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useServiceCategories() {
  return useQuery({
    queryKey: queryKeys.serviceCategories,
    queryFn: async (): Promise<string[]> => {
      const { data } = await apiClient.get('/services/categories');
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useService(id: string) {
  return useQuery({
    queryKey: queryKeys.service(id),
    queryFn: async (): Promise<ServiceDefinition> => {
      const { data } = await apiClient.get(`/services/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

// ===========================================================================
// Services Mutations
// ===========================================================================

export interface CreateServiceRequirementData {
  resourceTypeId: string;
  quantity: number;
  isRequired?: boolean;
  priority?: number;
  selectionCriteria?: Record<string, unknown>;
  notes?: string;
}

export interface CreateServiceData {
  name: string;
  code: string;
  description?: string;
  category?: string;
  color?: string;
  durationMinutes: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  basePrice?: number;
  /** Max clienti per appuntamento (1 = individuale, >1 = servizio di gruppo) */
  maxClients?: number;
  requirements?: CreateServiceRequirementData[];
}

export function useCreateService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateServiceData): Promise<ServiceDefinition> => {
      const response = await apiClient.post('/services', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...data }: CreateServiceData & { id: string }): Promise<ServiceDefinition> => {
      const response = await apiClient.put(`/services/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/services/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.serviceCategories });
    },
  });
}

// ===========================================================================
// Appointments Hooks
// ===========================================================================

export function useAppointments(
  date: Date,
  resourceId?: string,
  status?: string,
  options?: { enabled?: boolean },
) {
  const timeZone = useTenantTimeZone();
  const dateStr = civilDayOf(date);

  return useQuery({
    enabled: options?.enabled ?? true,
    // Il fuso fa parte della chiave: cambiandolo cambia l'intervallo richiesto,
    // e la risposta in cache non e' piu' quella giusta per la giornata.
    queryKey: [...queryKeys.appointments(dateStr, resourceId, status), timeZone],
    queryFn: async (): Promise<Appointment[]> => {
      // La giornata e' quella dell'ORGANIZZAZIONE. Chiedere 00:00Z-23:59Z
      // sfaserebbe la griglia dell'offset del tenant: per Roma perderebbe gli
      // appuntamenti fra mezzanotte e le 2, includendo quelli del giorno dopo.
      const start = startOfCivilDayInTz(dateStr, timeZone).toISOString();
      const end = lastInstantOfCivilDay(dateStr, timeZone).toISOString();
      
      const params: Record<string, string> = { start, end };
      if (resourceId) params.resourceId = resourceId;
      if (status) params.status = status;
      
      const { data } = await apiClient.get('/appointments', { params });
      return data;
    },
    staleTime: 30 * 1000, // 30 seconds - appointments can change frequently
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Appuntamenti su un intervallo di date civili del tenant, per la vista agenda.
 * Gli estremi sono INCLUSIVI: `endDate` e' l'ultimo giorno mostrato.
 *
 * Come per la vista giornaliera i confini sono calcolati nel fuso
 * dell'organizzazione: chiedere 00:00Z sfaserebbe l'elenco dell'offset del
 * tenant, tagliando fuori i primi appuntamenti del giorno.
 */
export function useAppointmentsRange(
  startDate: CivilDate,
  endDate: CivilDate,
  options?: { enabled?: boolean },
) {
  const timeZone = useTenantTimeZone();

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: [...queryKeys.appointmentsRange(startDate, endDate), timeZone],
    queryFn: async (): Promise<Appointment[]> => {
      const start = startOfCivilDayInTz(startDate, timeZone).toISOString();
      const end = lastInstantOfCivilDay(endDate, timeZone).toISOString();

      const { data } = await apiClient.get('/appointments', { params: { start, end } });
      return data;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/** Dimensione pagina dell'agenda "da oggi in poi" (il backend accetta max 100). */
export const UPCOMING_PAGE_SIZE = 50;

interface UpcomingAppointmentsPage {
  data: Appointment[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Elenco aperto degli appuntamenti da oggi in avanti, una pagina alla volta.
 *
 * Il punto di partenza e' la mezzanotte di oggi nel fuso dell'ORGANIZZAZIONE:
 * cosi' "oggi" e' la giornata dell'attivita', non quella del browser, e la
 * prima pagina include anche gli appuntamenti gia' passati di stamattina.
 */
export function useUpcomingAppointments(options?: { enabled?: boolean }) {
  const timeZone = useTenantTimeZone();
  const from = startOfCivilDayInTz(todayInTz(timeZone), timeZone).toISOString();

  return useInfiniteQuery({
    enabled: options?.enabled ?? true,
    queryKey: [...queryKeys.appointmentsUpcoming(from), timeZone],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<UpcomingAppointmentsPage> => {
      const { data } = await apiClient.get('/appointments/upcoming', {
        params: { from, limit: UPCOMING_PAGE_SIZE, offset: pageParam },
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.offset + lastPage.data.length;
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 30 * 1000,
  });
}

export function useAppointment(id: string) {
  return useQuery({
    queryKey: queryKeys.appointment(id),
    queryFn: async (): Promise<Appointment> => {
      const { data } = await apiClient.get(`/appointments/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

// ===========================================================================
// Appointments Mutations
// ===========================================================================

interface CustomResourceRequirementInput {
  resourceTypeId: string;
  quantity: number;
}

interface CreateAppointmentData {
  // Service-based appointment (optional when using custom fields)
  serviceDefinitionId?: string;

  // Custom event fields (required when no serviceDefinitionId)
  title?: string;
  description?: string;
  duration?: number;
  color?: string;
  price?: number;
  customResources?: CustomResourceRequirementInput[];

  // Common fields
  customerId?: string; // Optional for internal services (requiresClient = false) or custom events
  scheduledStart: string;
  scheduledEnd: string;
  allocations?: { resourceId: string; role?: string }[];
  status?: string;
  // Capienza evento di gruppo (default: maxClients del servizio, o 1 per custom)
  maxClients?: number;
  metadata?: Record<string, unknown>;
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateAppointmentData): Promise<Appointment> => {
      const response = await apiClient.post('/appointments', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate appointments queries to refetch
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      // Gli slot in cache sono calcolati PRIMA della creazione: senza invalidazione
      // il pannello riproporrebbe orari ormai occupati (e senza flag joinable)
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

interface UpdateAppointmentStatusData {
  id: string;
  status: string;
  cancellationReason?: string;
}

export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, status, cancellationReason }: UpdateAppointmentStatusData): Promise<Appointment> => {
      const response = await apiClient.put(`/appointments/${id}/status`, { 
        status, 
        cancellationReason 
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointment(variables.id) });
      // Una cancellazione libera le risorse: aggiorna gli slot in cache
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/appointments/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

// ===========================================================================
// Move/Update Appointment Hook (Drag & Drop or Edit Form)
// ===========================================================================

interface MoveAppointmentData {
  id: string;
  startTime?: string;
  resourceIds?: string[];
  notes?: string;
  status?: string;
  // Nuova capienza (non può scendere sotto l'occupazione attuale)
  maxClients?: number;
}

export function useMoveAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, startTime, resourceIds, notes, status, maxClients }: MoveAppointmentData): Promise<Appointment> => {
      const body: Record<string, unknown> = {};
      if (startTime !== undefined) body.startTime = startTime;
      if (resourceIds !== undefined) body.resourceIds = resourceIds;
      if (notes !== undefined) body.notes = notes;
      if (status !== undefined) body.status = status;
      if (maxClients !== undefined) body.maxClients = maxClients;

      const response = await apiClient.patch(`/appointments/${id}`, body);
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate appointments to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointment(variables.id) });
      // Spostamenti/cambi risorse alterano la disponibilità: slot in cache non più validi
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

// ===========================================================================
// Appointment Participants (eventi di gruppo)
// ===========================================================================

export function useAddParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appointmentId, customerId }: { appointmentId: string; customerId: string }): Promise<Appointment> => {
      const response = await apiClient.post(`/appointments/${appointmentId}/participants`, { customerId });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointment(variables.appointmentId) });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

export function useRemoveParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ appointmentId, participantId }: { appointmentId: string; participantId: string }): Promise<void> => {
      await apiClient.delete(`/appointments/${appointmentId}/participants/${participantId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointment(variables.appointmentId) });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

// ===========================================================================
// Customers Hooks
// ===========================================================================

export function useCustomers(search?: string) {
  return useQuery({
    queryKey: queryKeys.customers(search),
    queryFn: async (): Promise<PaginatedResponse<Customer>> => {
      const params: Record<string, string | number> = { limit: 50 };
      if (search) params.search = search;
      const { data } = await apiClient.get('/customers', { params });
      return data;
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: queryKeys.customer(id),
    queryFn: async (): Promise<Customer> => {
      const { data } = await apiClient.get(`/customers/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/** Storico appuntamenti (passati + futuri) di un cliente */
export function useCustomerAppointments(id: string | null) {
  return useQuery({
    queryKey: queryKeys.customerAppointments(id ?? ''),
    queryFn: async (): Promise<CustomerAppointmentsResponse> => {
      const { data } = await apiClient.get(`/customers/${id}/appointments`);
      return data;
    },
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

// ===========================================================================
// Customers Mutations
// ===========================================================================

export interface CreateCustomerData {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  internalNotes?: string;
}

export interface UpdateCustomerData extends Partial<CreateCustomerData> {}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateCustomerData): Promise<Customer> => {
      const response = await apiClient.post('/customers', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCustomerData }): Promise<Customer> => {
      const response = await apiClient.put(`/customers/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

// ===========================================================================
// Availability Hooks
// ===========================================================================

export interface AvailableSlot {
  start: string;
  end: string;
  durationMinutes: number;
  allocations: Array<{
    resourceTypeId: string;
    resourceTypeName: string;
    resourceId: string;
    resourceName: string;
  }>;
  /**
   * Presente se allo stesso orario esiste un evento di gruppo con posti liberi:
   * il cliente può essere aggiunto a quell'evento invece di crearne uno nuovo.
   * resourcesFree indica se ci sono anche risorse libere per creare un evento separato.
   */
  joinable?: {
    appointmentId: string;
    currentClients: number;
    maxClients: number;
    resourcesFree: boolean;
  };
}

export interface AvailabilityResponse {
  slots: AvailableSlot[];
  debug?: unknown;
}

// ===========================================================================
// Availability Criteria Types (for custom events)
// ===========================================================================

export interface CustomResourceRequirement {
  resourceTypeId: string;
  resourceId?: string | null;
  quantity?: number;
  isRequired?: boolean;
}

export interface SpecificResourceRequirement {
  resourceId: string;
}

export interface AvailabilityCriteria {
  duration?: number;
  serviceId?: string;
  customRequirements?: CustomResourceRequirement[];
  specificResources?: SpecificResourceRequirement[];
  bufferBefore?: number;
  bufferAfter?: number;
}

interface FindSlotsParams {
  serviceDefinitionId?: string;
  criteria?: AvailabilityCriteria;
  dateRange: {
    start: string;
    end: string;
  };
  maxSlots?: number;
  /** Includi gli eventi di gruppo esistenti con posti liberi (flag joinable sugli slot) */
  includeJoinableEvents?: boolean;
  // Smart Scheduler Constraints
  allowedDays?: number[];
  minTime?: string;
  maxTime?: string;
  deadlineDate?: string;
}

export function useAvailableSlots(params: FindSlotsParams | null) {
  return useQuery({
    queryKey: ['availableSlots', params],
    queryFn: async (): Promise<AvailabilityResponse> => {
      if (!params) throw new Error('No params');
      
      // Build request body — serviceDefinitionId and criteria can coexist
      // (criteria.customRequirements override service DB requirements)
      const requestBody: Record<string, unknown> = {
        dateRange: params.dateRange,
        maxSlots: params.maxSlots ?? 20,
        slotIntervalMinutes: 15,
      };

      if (params.serviceDefinitionId) {
        requestBody.serviceDefinitionId = params.serviceDefinitionId;
      }
      if (params.criteria) {
        requestBody.criteria = params.criteria;
      }
      if (params.includeJoinableEvents) {
        requestBody.includeJoinableEvents = true;
      }

      // Smart Scheduler Constraints
      if (params.allowedDays?.length) {
        requestBody.allowedDays = params.allowedDays;
      }
      if (params.minTime) {
        requestBody.minTime = params.minTime;
      }
      if (params.maxTime) {
        requestBody.maxTime = params.maxTime;
      }
      if (params.deadlineDate) {
        requestBody.deadlineDate = params.deadlineDate;
      }

      const { data } = await apiClient.post('/availability/slots', requestBody);
      return data;
    },
    // Enable query if we have either serviceDefinitionId OR criteria with duration
    enabled: !!(
      (params?.serviceDefinitionId || (params?.criteria?.duration && params?.criteria?.duration > 0)) 
      && params?.dateRange?.start
    ),
    staleTime: 30 * 1000, // 30 seconds - slots can change
    refetchOnWindowFocus: true,
  });
}

// ===========================================================================
// Optimization Hooks
// ===========================================================================

export interface BlockingAppointment {
  id: string;
  serviceName: string;
  customerName: string;
  currentStart: string;
  currentEnd: string;
  resources: string[];
}

export interface SuggestedMove {
  newStart: string;
  newEnd: string;
  availableResources: Array<{
    resourceId: string;
    resourceName: string;
  }>;
}

export interface OptimizeSuggestionResponse {
  canOptimize: boolean;
  reason?: string;
  blockingAppointment?: BlockingAppointment;
  suggestedMove?: SuggestedMove;
  freedResources?: string[];
}

interface OptimizeSuggestionParams {
  desiredDateTime: string;
  serviceDefinitionId: string;
  durationMinutes?: number;
  preferredResourceIds?: string[];
}

export function useOptimizeSuggestion() {
  return useMutation({
    mutationFn: async (params: OptimizeSuggestionParams): Promise<OptimizeSuggestionResponse> => {
      const { data } = await apiClient.post('/scheduling/optimize-suggestion', params);
      return data;
    },
  });
}

// ===========================================================================
// Override Booking - Transactional Endpoint (Move + Create Atomically)
// ===========================================================================

interface OverrideBookingParams {
  newAppointment: {
    serviceDefinitionId: string;
    customerId: string;
    scheduledStart: string;
    scheduledEnd: string;
    allocations: Array<{ resourceId: string; role?: string }>;
  };
  moveAppointmentId: string;
  moveToTime: string;
}

export interface OverrideBookingResponse {
  movedAppointment: {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    serviceDefinition: { id: string; name: string };
    customer: { id: string; firstName: string; lastName: string };
  };
  newAppointment: {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    serviceDefinition: { id: string; name: string };
    customer: { id: string; firstName: string; lastName: string };
  };
}

export function useOverrideBooking() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: OverrideBookingParams): Promise<OverrideBookingResponse> => {
      const { data } = await apiClient.post('/appointments/override-booking', params);
      return data;
    },
    onSuccess: () => {
      // Invalidate appointments and availability to refresh the grid
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

// ===========================================================================
// Legacy Apply Optimization (kept for backward compatibility)
// ===========================================================================

interface ApplyOptimizationParams {
  blockingAppointmentId: string;
  newStartTime: string;
  newAppointment: {
    serviceDefinitionId: string;
    customerId: string;
    scheduledStart: string;
    scheduledEnd: string;
    allocations: Array<{ resourceId: string; role?: string }>;
  };
}

export interface ApplyOptimizationResponse {
  success: boolean;
  movedAppointment: {
    id: string;
    newStart: string;
    newEnd: string;
  };
  newAppointment: {
    id: string;
    serviceName: string;
    customerName: string;
    start: string;
    end: string;
  };
}

/**
 * @deprecated Use useOverrideBooking instead - single transactional endpoint
 */
export function useApplyOptimization() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: ApplyOptimizationParams): Promise<ApplyOptimizationResponse> => {
      const { data } = await apiClient.post('/scheduling/apply-optimization', params);
      return data;
    },
    onSuccess: () => {
      // Invalidate appointments and availability to refresh the grid
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

// ===========================================================================
// Dashboard Hooks
// ===========================================================================

export interface DashboardChartDataPoint {
  date: string;
  fullDate: string;
  count: number;
  revenue: number;
}

export interface DashboardTopService {
  serviceId: string;
  serviceName: string;
  color: string | null;
  count: number;
}

export interface DashboardUpcomingAppointment {
  id: string;
  title: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
  price: number;
  color: string;
  customer: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  service: {
    id: string;
    name: string;
  } | null;
}

export interface DashboardStats {
  totalAppointments: number;
  totalRevenue: number;
  averageAppointmentValue: number;
  cancelledCount: number;
  completedCount: number;
  trends: {
    appointmentsTrend: number | null;
    revenueTrend: number | null;
  };
  statusBreakdown: Array<{ status: string; count: number }>;
  topServices: DashboardTopService[];
  chartData: DashboardChartDataPoint[];
  upcomingAppointments: DashboardUpcomingAppointment[];
  period: {
    start: string;
    end: string;
    label: string;
  };
}

export function useDashboardStats(days: number = 30) {
  return useQuery({
    queryKey: queryKeys.dashboardStats(days),
    queryFn: async (): Promise<DashboardStats> => {
      const { data } = await apiClient.get('/dashboard/stats', {
        params: { days },
      });
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

export interface ResourceUtilization {
  resourceId: string;
  name: string;
  type: string;
  color: string | null;
  pastUtilization: number;
  futureUtilization: number;
}

export function useDashboardUtilization() {
  return useQuery({
    queryKey: queryKeys.dashboardUtilization,
    queryFn: async (): Promise<ResourceUtilization[]> => {
      const { data } = await apiClient.get('/dashboard/utilization');
      return data;
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export interface ReorganizationItem {
  appointmentId: string;
  allocationId: string;
  scheduledStart: string;
  scheduledEnd: string;
  status: string;
  title: string;
  serviceName: string | null;
  customerName: string | null;
  resourceId: string;
  resourceName: string;
  resourceColor: string | null;
  absenceTitle: string;
  absenceType: string;
}

/**
 * Appuntamenti da riorganizzare perché la risorsa allocata ha un'assenza
 * che copre l'orario dell'appuntamento.
 */
export function useReorganizationNeeded() {
  return useQuery({
    queryKey: ['dashboardReorganization'],
    queryFn: async (): Promise<ReorganizationItem[]> => {
      const { data } = await apiClient.get('/dashboard/reorganization');
      return data;
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// ===========================================================================
// Reports Hooks
// ===========================================================================

export interface ReportsUtilizationParams {
  startDate: string; // ISO date string
  endDate: string;
  resourceIds?: string[];
  groupBy: 'day' | 'week' | 'month';
}

export interface ReportsUtilizationResource {
  id: string;
  name: string;
  type: string;
  color: string | null;
  resourceTypeId: string;
  resourceTypeColor: string | null;
}

export interface ReportsUtilizationDataPoint {
  period: string;
  periodStart: string;
  [resourceName: string]: string | number;
}

export interface ReportsUtilizationSummary {
  totalHours: number;
  totalAppointments: number;
  averageHoursPerResource: number;
  busiestResource: { name: string; hours: number; color: string | null } | null;
  averageHoursPerDay: number;
  resourceCount: number;
}

export interface ReportsUtilizationResult {
  data: ReportsUtilizationDataPoint[];
  resources: ReportsUtilizationResource[];
  summary: ReportsUtilizationSummary;
  params: {
    startDate: string;
    endDate: string;
    groupBy: 'day' | 'week' | 'month';
  };
}

// --- Peak Hours (heatmap) ---

export interface PeakHoursParams {
  startDate: string;
  endDate: string;
}

export interface PeakHoursResult {
  matrix: number[][];   // 7 righe (lun→dom) × N ore
  hourStart: number;
  hourEnd: number;
  maxCount: number;
  totalAppointments: number;
  busiestSlot: { day: number; hour: number; count: number } | null;
  params: { startDate: string; endDate: string };
}

export function useReportsUtilization(params: ReportsUtilizationParams) {
  return useQuery({
    queryKey: queryKeys.reportsUtilization(params),
    queryFn: async (): Promise<ReportsUtilizationResult> => {
      const { data } = await apiClient.get('/reports/utilization', {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          resourceIds: params.resourceIds,
          groupBy: params.groupBy,
        },
      });
      return data;
    },
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useReportsPeakHours(params: PeakHoursParams) {
  return useQuery({
    queryKey: queryKeys.reportsPeakHours(params),
    queryFn: async (): Promise<PeakHoursResult> => {
      const { data } = await apiClient.get('/reports/peak-hours', {
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
        },
      });
      return data;
    },
    enabled: !!params.startDate && !!params.endDate,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// ===========================================================================
// Tenant / Organization Hooks
// ===========================================================================

export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  country: string;
  /// Timezone IANA: fonte di verita' per come vengono interpretati gli orari
  /// di apertura e in che fuso sono mostrati gli appuntamenti.
  timezone: string;
  settings: TenantSettings;
  branding: OrgBranding;
  isActive: boolean;
  subscriptionTier: string;
  trialEndsAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DayHours {
  start: string;
  end: string;
}

export interface BusinessHours {
  monday?: DayHours | null;
  tuesday?: DayHours | null;
  wednesday?: DayHours | null;
  thursday?: DayHours | null;
  friday?: DayHours | null;
  saturday?: DayHours | null;
  sunday?: DayHours | null;
}

export interface BookingRules {
  minAdvanceHours?: number;
  maxAdvanceDays?: number;
  cancellationHours?: number;
}

export interface TenantSettings {
  businessHours?: BusinessHours;
  timezone?: string;
  locale?: string;
  currency?: string;
  bookingRules?: BookingRules;
  [key: string]: unknown;
}

export interface UpdateTenantData {
  name?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}

export interface UpdateTenantSettingsData {
  businessHours?: BusinessHours;
  timezone?: string;
  locale?: string;
  currency?: string;
  bookingRules?: BookingRules;
}

/**
 * Identificativi timezone supportati dal server. Arrivano dal suo tzdata, cioe'
 * esattamente il set che la validazione accetta: il selettore non puo' proporre
 * un valore che verrebbe poi rifiutato.
 */
export function useSupportedTimeZones() {
  return useQuery({
    queryKey: ['timezones'],
    queryFn: async (): Promise<string[]> => {
      const { data } = await apiClient.get('/tenants/timezones');
      return data;
    },
    // L'elenco cambia solo con gli aggiornamenti del tzdata: inutile rifarlo.
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useTenantDetails() {
  return useQuery({
    queryKey: ['tenant-details'],
    queryFn: async (): Promise<TenantDetail> => {
      const { data } = await apiClient.get('/tenants/current');
      return data;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useUpdateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTenantData): Promise<TenantDetail> => {
      const response = await apiClient.patch('/tenants/current', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
}

/**
 * Identità visiva: logo caricato (data URL) oppure icona standard + colore.
 * `logo: null` rimuove il logo, `logo` omesso lo lascia invariato.
 */
export interface UpdateTenantBrandingData {
  icon?: string | null;
  color?: string | null;
  logo?: string | null;
}

export function useUpdateTenantBranding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTenantBrandingData): Promise<TenantDetail> => {
      const response = await apiClient.patch('/tenants/current/branding', data, {
        // L'upload di un logo è più pesante di una normale chiamata JSON
        timeout: 30000,
      });
      return response.data;
    },
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
      // Lo switcher e la pagina di selezione leggono il branding dalle
      // memberships persistite: allineale subito senza attendere un reload.
      const store = useTenantStore.getState();
      store.setMemberships(
        store.memberships.map((m) =>
          m.organizationId === tenant.id ? { ...m, branding: tenant.branding } : m,
        ),
      );
    },
  });
}

// ---------------------------------------------------------------------------
// Eliminazione definitiva dell'organizzazione (solo OWNER)
// ---------------------------------------------------------------------------

export interface TenantDeletionSummary {
  members: number;
  pendingInvites: number;
  customers: number;
  resources: number;
  services: number;
  appointments: number;
  bookingRequests: number;
}

export interface DeleteTenantResult {
  id: string;
  name: string;
  /// Momento dell'eliminazione (soft delete)
  deletedAt: string;
  /// Data della cancellazione definitiva: fino ad allora e' ripristinabile
  purgeAt: string;
}

export interface DeletedTenant {
  organizationId: string;
  tenantName: string;
  tenantSlug: string;
  deletedAt: string;
  purgeAt: string;
  branding: OrgBranding;
}

/**
 * Cosa verrebbe cancellato insieme all'organizzazione. Serve a far vedere
 * all'owner la portata dell'operazione prima che confermi.
 */
export function useTenantDeletionSummary(enabled = true) {
  return useQuery({
    queryKey: ['tenant-deletion-summary'],
    queryFn: async (): Promise<TenantDeletionSummary> => {
      const { data } = await apiClient.get('/tenants/current/deletion-summary');
      return data;
    },
    // Conteggi mostrati in un dialogo di conferma: vanno letti al momento,
    // non da una cache di qualche minuto fa.
    staleTime: 0,
    gcTime: 0,
    enabled,
  });
}

export function useDeleteTenant() {
  return useMutation({
    mutationFn: async (password: string): Promise<DeleteTenantResult> => {
      // DELETE con body: axios lo passa in `data`. La password non finisce
      // mai in query string.
      const { data } = await apiClient.delete('/tenants/current', {
        data: { password },
        // La cancellazione a cascata di un'organizzazione grande puo'
        // richiedere piu' del timeout standard.
        timeout: 60000,
      });
      return data;
    },
  });
}

/**
 * Organizzazioni eliminate ma ancora ripristinabili (solo quelle di cui sono
 * OWNER). Non richiede un tenant attivo: l'endpoint salta il TenantGuard.
 */
export function useMyDeletedTenants(enabled = true) {
  return useQuery({
    queryKey: queryKeys.myDeletedTenants,
    queryFn: async (): Promise<DeletedTenant[]> => {
      const { data } = await apiClient.get('/tenants/my-deleted');
      return data;
    },
    staleTime: 60 * 1000,
    enabled,
  });
}

export function useRestoreTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string): Promise<TenantMembership> => {
      const { data } = await apiClient.post(
        `/tenants/my-deleted/${organizationId}/restore`,
      );
      return data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myDeletedTenants });
      // L'organizzazione torna selezionabile: va rimessa nello switcher
      await refreshMyMemberships();
    },
  });
}

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTenantSettingsData): Promise<TenantDetail> => {
      const response = await apiClient.patch('/tenants/current/settings', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
}

// ===========================================================================
// Booking Requests (admin dashboard)
// ===========================================================================

export interface BookingRequestListResponse {
  data: (BookingRequest & {
    serviceName: string;
    matchedCustomer: {
      id: string;
      firstName: string;
      lastName: string;
      email: string | null;
      phone: string | null;
    } | null;
  })[];
  total: number;
  limit: number;
  offset: number;
  pendingCount: number;
}

export interface BookingRequestsQuery {
  status?: BookingRequestStatus[];
  fromDate?: string;
  toDate?: string;
  serviceDefinitionId?: string;
  limit?: number;
  offset?: number;
}

export const bookingRequestsKeys = {
  list: (query: BookingRequestsQuery) => ['booking-requests', 'list', query] as const,
  detail: (id: string) => ['booking-requests', 'detail', id] as const,
  pendingCount: ['booking-requests', 'pending-count'] as const,
};

export function useBookingRequests(query: BookingRequestsQuery = {}) {
  return useQuery({
    queryKey: bookingRequestsKeys.list(query),
    queryFn: async (): Promise<BookingRequestListResponse> => {
      const params: Record<string, unknown> = {};
      if (query.status?.length) params.status = query.status;
      if (query.fromDate) params.fromDate = query.fromDate;
      if (query.toDate) params.toDate = query.toDate;
      if (query.serviceDefinitionId) params.serviceDefinitionId = query.serviceDefinitionId;
      if (query.limit !== undefined) params.limit = query.limit;
      if (query.offset !== undefined) params.offset = query.offset;
      const { data } = await apiClient.get('/booking-requests', { params });
      return data;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useBookingRequest(id: string | null | undefined) {
  return useQuery({
    queryKey: bookingRequestsKeys.detail(id ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get(`/booking-requests/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

/**
 * Conteggio pending per il badge in header. Riusa l'endpoint list con limit=1
 * sfruttando il campo aggregato `pendingCount`.
 */
export function useBookingRequestsPendingCount() {
  return useQuery({
    queryKey: bookingRequestsKeys.pendingCount,
    queryFn: async (): Promise<number> => {
      const { data } = await apiClient.get<BookingRequestListResponse>('/booking-requests', {
        params: { limit: 1, status: ['PENDING'] },
      });
      return data.pendingCount ?? 0;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export interface ApproveBookingRequestPayload {
  /** Solo per richieste di join: 'join' (default) unisce all'evento, 'new_appointment' crea un appuntamento separato */
  approveAs?: 'join' | 'new_appointment';
  allocationsOverride?: Array<{
    resourceTypeId: string;
    resourceId: string;
    startTime: string;
    endTime: string;
    role?: 'primary' | 'support' | 'facility';
  }>;
  forceCustomerId?: string;
  reviewNotes?: string;
}

export function useApproveBookingRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: ApproveBookingRequestPayload }) => {
      const { data } = await apiClient.post(`/booking-requests/${id}/approve`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-requests'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      // L'approvazione crea l'appuntamento: gli slot in cache diventano stantii
      queryClient.invalidateQueries({ queryKey: ['availableSlots'] });
    },
  });
}

export interface RejectBookingRequestPayload {
  rejectionReason?: string;
  reviewNotes?: string;
}

export function useRejectBookingRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: RejectBookingRequestPayload }) => {
      const { data } = await apiClient.post(`/booking-requests/${id}/reject`, payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking-requests'] });
    },
  });
}

/**
 * Update di Tenant.settings.publicBooking (deep-merge lato server).
 */
export function useUpdatePublicBookingSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (publicBooking: Partial<PublicBookingSettings>): Promise<TenantDetail> => {
      const { data } = await apiClient.patch('/tenants/current/settings', { publicBooking });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
}
